import { Injectable, NgZone } from '@angular/core';
import { getMessaging, getToken, onMessage, Messaging } from '@angular/fire/messaging';
import { getApp } from '@angular/fire/app';
import { Observable, BehaviorSubject, from } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';

@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {
    private messaging: Messaging | null = null;
    private tokenSubject = new BehaviorSubject<string | null>(null);
    public token$ = this.tokenSubject.asObservable();
    private messageSubject = new BehaviorSubject<any>(null);
    public message$ = this.messageSubject.asObservable();
    
    // Storage key for FCM token
    private readonly TOKEN_STORAGE_KEY = 'fcm_token';
    private readonly TOKEN_TIMESTAMP_KEY = 'fcm_token_timestamp';
    
    // Token expiry time: 30 days (tokens can change but typically don't expire)
    private readonly TOKEN_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

    constructor(private ngZone: NgZone) {
        // Initialize messaging asynchronously to ensure Firebase app is ready
        setTimeout(() => {
            this.initializeMessaging();
        }, 0);
    }

    /**
     * Initialize Firebase Messaging
     */
    private async initializeMessaging(): Promise<void> {
        try {
            const app = getApp();
            this.messaging = getMessaging(app);
        } catch (error) {
            console.error('Error initializing Firebase Messaging:', error);
            // Retry after a short delay if app is not ready
            setTimeout(() => {
                try {
                    const app = getApp();
                    this.messaging = getMessaging(app);
                } catch (retryError) {
                    console.error('Error retrying Firebase Messaging initialization:', retryError);
                }
            }, 1000);
        }
    }

    /**
     * Get stored token from localStorage
     */
    private getStoredToken(): string | null {
        try {
            const storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
            
            if (!storedToken) {
                return null;
            }
            return storedToken;
        } catch (error) {
            console.error('Error reading stored token:', error);
            return null;
        }
    }

    /**
     * Store token in localStorage
     */
    private storeToken(token: string): void {
        try {
            localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
            localStorage.setItem(this.TOKEN_TIMESTAMP_KEY, Date.now().toString());
        } catch (error) {
            console.error('Error storing token:', error);
        }
    }

    /**
     * Clear stored token
     */
    private clearStoredToken(): void {
        try {
            localStorage.removeItem(this.TOKEN_STORAGE_KEY);
            localStorage.removeItem(this.TOKEN_TIMESTAMP_KEY);
        } catch (error) {
            console.error('Error clearing stored token:', error);
        }
    }

    /**
     * Check if token has changed
     */
    private hasTokenChanged(newToken: string): boolean {
        const storedToken = this.getStoredToken();
        return storedToken !== newToken;
    }

    /**
     * Request notification permission and get FCM token
     * Returns existing token if valid, otherwise requests a new one
     */
    async requestPermission(forceRefresh: boolean = false): Promise<string | null> {
        if (!this.messaging) {
            await this.initializeMessaging();
        }

        if (!this.messaging) {
            console.error('Firebase Messaging not initialized');
            return null;
        }

        try {
            // Check if we have a valid stored token
            if (!forceRefresh) {
                const storedToken = this.getStoredToken();
                if (storedToken) {
                    console.log('Using stored FCM token');
                    this.ngZone.run(() => {
                        this.tokenSubject.next(storedToken);
                    });
                    return storedToken;
                }
            }

            // Request permission first
            const permission = await Notification.requestPermission();
            
            if (permission !== 'granted') {
                console.warn('Notification permission denied');
                return null;
            }

            // Register service worker and wait for it to be active
            const swRegistration = await this.registerServiceWorker();
            
            if (!swRegistration || !swRegistration.active) {
                console.error('Service Worker is not active. Cannot get FCM token.');
                return null;
            }
            
            // Get FCM token - provide service worker registration
            const tokenOptions: any = {
                vapidKey: this.getVapidKey(),
                serviceWorkerRegistration: swRegistration
            };
            
            const token = await getToken(this.messaging, tokenOptions);
            
            if (token) {
                // Check if token has changed
                const tokenChanged = this.hasTokenChanged(token);
                
                // Store the new token
                this.storeToken(token);
                
                this.ngZone.run(() => {
                    this.tokenSubject.next(token);
                });
                
                if (tokenChanged) {
                    console.log('FCM Token obtained (new or changed):', token);
                } else {
                    console.log('FCM Token obtained:', token);
                }
                
                return token;
            } else {
                console.warn('No FCM token available');
                return null;
            }
        } catch (error) {
            console.error('Error getting FCM token:', error);
            return null;
        }
    }

    /**
     * Get current token (from storage or request new one)
     */
    async getCurrentToken(): Promise<string | null> {
        // Try to get stored token first
        const storedToken = this.getStoredToken();
        if (storedToken) {
            this.ngZone.run(() => {
                this.tokenSubject.next(storedToken);
            });
            return storedToken;
        }
        
        // If no stored token, request permission and get new token
        return await this.requestPermission();
    }

    /**
     * Refresh FCM token (force new token)
     */
    async refreshToken(): Promise<string | null> {
        this.clearStoredToken();
        return await this.requestPermission(true);
    }

    /**
     * Clear FCM token (on logout)
     */
    clearToken(): void {
        this.clearStoredToken();
        this.ngZone.run(() => {
            this.tokenSubject.next(null);
        });
    }

    /**
     * Register service worker for Firebase messaging
     * Firebase will automatically use this service worker for messaging
     */
    private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
        if (!('serviceWorker' in navigator)) {
            console.warn('Service Workers are not supported in this browser');
            return null;
        }

        try {
            // Check if there's already a service worker registered
            let registration = await navigator.serviceWorker.getRegistration();
            
            if (!registration) {
                // Register the service worker at root scope
                registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                    scope: '/'
                });
                console.log('Service Worker registered successfully:', registration);
            } else {
                console.log('Service Worker already registered:', registration);
            }
            
            // Wait for the service worker to be active with timeout
            const waitForActive = async (reg: ServiceWorkerRegistration, timeoutMs: number = 10000): Promise<boolean> => {
                if (reg.active) {
                    return true;
                }

                return new Promise<boolean>((resolve) => {
                    const startTime = Date.now();
                    
                    const checkActive = () => {
                        if (reg.active) {
                            resolve(true);
                            return;
                        }
                        
                        if (Date.now() - startTime > timeoutMs) {
                            console.warn('Service Worker activation timeout');
                            resolve(false);
                            return;
                        }
                        
                        setTimeout(checkActive, 100);
                    };

                    // Listen for state changes
                    if (reg.installing) {
                        reg.installing.addEventListener('statechange', () => {
                            if (reg.installing?.state === 'activated' || reg.active) {
                                resolve(true);
                            } else {
                                checkActive();
                            }
                        });
                    } else if (reg.waiting) {
                        // Try to activate waiting service worker
                        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
                        reg.waiting.addEventListener('statechange', () => {
                            if (reg.waiting?.state === 'activated' || reg.active) {
                                resolve(true);
                            } else {
                                checkActive();
                            }
                        });
                    }
                    
                    checkActive();
                });
            };

            // Wait for service worker to be active
            if (!registration.active) {
                console.log('Waiting for Service Worker to be active...');
                const isActive = await waitForActive(registration);
                if (!isActive) {
                    console.error('Service Worker failed to activate within timeout');
                    return null;
                }
            }
            
            // Double check we have an active service worker
            if (!registration.active) {
                console.error('Service Worker is not active after waiting');
                return null;
            }
            
            console.log('Service Worker is active and ready');
            
            return registration;
        } catch (error) {
            console.error('Error registering service worker:', error);
            // Try fallback registration without explicit scope
            try {
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                console.log('Service Worker registered (fallback):', registration);
                
                // Wait for activation with timeout
                if (!registration.active) {
                    const startTime = Date.now();
                    const timeout = 10000; // 10 seconds
                    
                    await new Promise<void>((resolve, reject) => {
                        const checkActive = () => {
                            if (registration.active) {
                                resolve();
                            } else if (Date.now() - startTime > timeout) {
                                reject(new Error('Service Worker activation timeout'));
                            } else {
                                setTimeout(checkActive, 100);
                            }
                        };
                        checkActive();
                    });
                }
                
                return registration;
            } catch (fallbackError) {
                console.error('Error registering service worker (fallback):', fallbackError);
                return null;
            }
        }
    }

    /**
     * Get VAPID key from environment or use default
     * Note: You should add this to your environment file
     */
    private getVapidKey(): string | undefined {
        // TODO: Add VAPID key to environment file
        // For now, return undefined to use default
        return undefined;
    }

    /**
     * Subscribe to foreground messages
     */
    subscribeToMessages(): void {
        if (!this.messaging) {
            console.error('Firebase Messaging not initialized');
            return;
        }

        onMessage(this.messaging, (payload) => {
            this.ngZone.run(() => {
                console.log('Foreground message received:', payload);
                
                // Show browser notification for foreground messages (system notification)
                this.showForegroundNotification(payload);
                
                // Emit to subscribers (for in-app popup)
                this.messageSubject.next(payload);
            });
        });
    }

    /**
     * Show browser notification for foreground messages
     * This creates a system notification that appears in the browser's notification center
     */
    private showForegroundNotification(payload: any): void {
        // Check if browser supports notifications
        if (!('Notification' in window)) {
            console.warn('This browser does not support notifications');
            return;
        }

        // Check if permission is granted
        if (Notification.permission !== 'granted') {
            console.warn('Notification permission not granted. Current permission:', Notification.permission);
            // Try to request permission again
            this.requestPermission().catch(err => {
                console.error('Failed to request notification permission:', err);
            });
            return;
        }

        const data = payload.data || {};
        const notification = payload.notification || {};

        // Extract notification details from your payload structure
        // Use data.event as the notification title and data.content as the body
        const title = notification.title || data.event || data.subject || 'New Notification';
        const body = notification.body || data.content || data.description || '';
        const icon = notification.icon || data.icon || '/assets/icons/icon-192x192.png';
        const image = notification.image || data.image;
        const tag = data.id || payload.messageId || `notification-${Date.now()}`;
        const requireInteraction = data.requireInteraction === 'true' || false;

        // Build the link URL - navigate to submitquote/{id}
        let linkUrl = data.link || data.url;
        if (!linkUrl && data.id) {
            linkUrl = `${window.location.origin}/submitquote/${data.id}`;
        } else if (!linkUrl && data.stock_id) {
            linkUrl = `${window.location.origin}/bid-management/offline-management?id=${data.stock_id}`;
        }

        // Play notification sound
        this.playNotificationSound();

        // Create notification options for system notification
        // Note: 'actions' are only supported for ServiceWorker notifications, not regular Notification API
        const options: NotificationOptions & { vibrate?: number[]; timestamp?: number; image?: string } = {
            body: body,
            icon: icon,
            tag: tag, // Use tag to replace notifications with the same ID
            badge: '/assets/icons/icon-96x96.png',
            requireInteraction: requireInteraction,
            silent: false, // Enable sound (browser will play default notification sound)
            vibrate: [200, 100, 200], // Vibration pattern (for mobile devices)
            timestamp: Date.now(),
            data: {
                ...data,
                link: linkUrl,
                useRouter: data.useRouter === 'true' || data.useRouter === true,
                messageId: payload.messageId,
                url: linkUrl
            }
        };

        // Add image if available (for rich notifications)
        if (image) {
            options.image = image;
        }

        // Show the system notification
        try {
            const notificationInstance = new Notification(title, options);

            console.log('Browser notification shown:', { title, body, tag });

            // Handle notification click - opens the app/window
            notificationInstance.onclick = (event) => {
                event.preventDefault();
                
                // Focus the window
                window.focus();
                
                // Navigate if link is provided
                if (linkUrl) {
                    // Ensure linkUrl is a full URL or absolute path
                    let navigationUrl = linkUrl;
                    
                    // If it's a relative path, make it absolute
                    if (linkUrl.startsWith('/')) {
                        navigationUrl = window.location.origin + linkUrl;
                    } else if (!linkUrl.startsWith('http://') && !linkUrl.startsWith('https://')) {
                        // If it's just an ID or relative path, construct full URL
                        navigationUrl = window.location.origin + '/submitquote/' + linkUrl;
                    }
                    
                    console.log('Navigating to:', navigationUrl);
                    
                    // Use window.location for navigation (works for both same-origin and cross-origin)
                    window.location.href = navigationUrl;
                }
                
                // Close the notification
                notificationInstance.close();
            };

            // Handle notification close
            notificationInstance.onclose = () => {
                console.log('Notification closed:', tag);
            };

            // Handle notification error
            notificationInstance.onerror = (error) => {
                console.error('Notification error:', error);
            };

            // Auto-close after 5 seconds if not requiring interaction
            if (!requireInteraction) {
                setTimeout(() => {
                    notificationInstance.close();
                }, 5000);
            }
        } catch (error) {
            console.error('Error showing browser notification:', error);
        }
    }

    /**
     * Play notification sound
     */
    private playNotificationSound(): void {
        try {
            // Create audio element and play sound
            const audio = new Audio();
            
            // Try to use a notification sound file if available
            // Fallback to a simple beep using Web Audio API
            audio.src = '/assets/sounds/notification.mp3';
            audio.volume = 0.5;
            
            audio.play().catch(error => {
                console.warn('Could not play notification sound file, using beep:', error);
                // Fallback: Use Web Audio API to create a beep sound
                this.playBeepSound();
            });
        } catch (error) {
            console.warn('Error playing notification sound:', error);
            // Fallback: Use Web Audio API to create a beep sound
            this.playBeepSound();
        }
    }

    /**
     * Play a simple beep sound using Web Audio API
     */
    private playBeepSound(): void {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 800; // Frequency in Hz
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
            console.warn('Could not play beep sound:', error);
        }
    }

    /**
     * Convert Firebase message payload to Notification format
     */
    convertToNotification(payload: any): Notification {
        const data = payload.data || {};
        const notification = payload.notification || {};
        
        // Build the link URL - navigate to submitquote/{id}
        let linkUrl = data.link || data.url;
        if (!linkUrl && data.id) {
            linkUrl = `/submitquote/${data.id}`;
        } else if (!linkUrl && data.stock_id) {
            linkUrl = `/bid-management/offline-management?id=${data.stock_id}`;
        }
        
        return {
            id: data.id || payload.messageId || `notification-${Date.now()}`,
            title: notification.title || data.event || data.subject || 'New Notification',
            description: notification.body || data.content || data.description || '',
            time: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            link: linkUrl || undefined,
            useRouter: data.useRouter !== 'false' && data.useRouter !== false, // Default to using router
            read: false,
            icon: notification.icon || data.icon,
            image: notification.image || data.image,
            // Additional fields from API response
            ...(data.lotNo && { lotNo: data.lotNo }),
            ...(data.messageTitle && { messageTitle: data.messageTitle }),
            ...(data.code && { code: data.code }),
            ...(data.event && { event: data.event })
        };
    }

    /**
     * Get current FCM token synchronously (from BehaviorSubject)
     * Use this when you need the current token value without async operations
     */
    getTokenSync(): string | null {
        return this.tokenSubject.value;
    }
}

